import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    AiFillTag,
    AiFillExperiment,
    AiFillDollarCircle,
    AiOutlineArrowUp,
    AiOutlineArrowDown,
} from "react-icons/ai";
import {
    getGuns,
    getInvoices,
    getRepairs,
    getUsers,
} from "../services/statisticsService";

function Statistic() {
    const [stats, setStats] = useState({
        totalGuns: 0,
        totalSales: 0,
        completedRepairs: 0,
        totalRevenue: 0,
        totalUsers: 0,
    });
    const [salesGrowth, setSalesGrowth] = useState(0);
    const [revenueGrowth, setRevenueGrowth] = useState(0);
    const [gunsInStock, setGunsInStock] = useState(0);
    const [totalSales, setTotalSales] = useState(0);

    useEffect(() => {
        fetchStatistics();
        fetchGunsInStock();
        fetchSalesData();
    }, []);

    // ✅ Fetch Guns in Stock
    const fetchGunsInStock = async () => {
        try {
            const gunsResponse = await getGuns();
            const inStockGuns = gunsResponse.filter((gun) => gun.status === "In Stock");
            setGunsInStock(inStockGuns.length);
        } catch (error) {
            console.error("Error fetching guns:", error);
        }
    };

    // ✅ Fetch Sales Data from Invoices (instead of orders)
    const fetchSalesData = async () => {
        try {
            const invoicesResponse = await getInvoices(); // ✅ Fetch invoices instead of sales

            if (!invoicesResponse.success) return;

            const now = new Date();
            const oneWeekAgo = new Date();
            oneWeekAgo.setDate(now.getDate() - 7);
            const twoWeeksAgo = new Date();
            twoWeeksAgo.setDate(now.getDate() - 14);

            // ✅ Filter invoices based on date
            const currentWeekInvoices = invoicesResponse.data.filter((invoice) => {
                const invoiceDate = new Date(invoice.created_at);
                return (
                    invoiceDate >= oneWeekAgo &&
                    invoiceDate <= now &&
                    invoice.payment_status === "Completed"
                );
            });

            const previousWeekInvoices = invoicesResponse.data.filter((invoice) => {
                const invoiceDate = new Date(invoice.created_at);
                return (
                    invoiceDate >= twoWeeksAgo &&
                    invoiceDate < oneWeekAgo &&
                    invoice.payment_status === "Completed"
                );
            });

            // ✅ Calculate total sales
            const currentWeekSales = currentWeekInvoices.reduce(
                (acc, invoice) => acc + parseFloat(invoice.final_amount),
                0
            );

            const previousWeekSales = previousWeekInvoices.reduce(
                (acc, invoice) => acc + parseFloat(invoice.final_amount),
                0
            );

            // ✅ Calculate Growth %
            let growthPercentage = previousWeekSales !== 0
                ? ((currentWeekSales - previousWeekSales) / previousWeekSales) * 100
                : 0;

            setTotalSales(currentWeekSales);
            setSalesGrowth(growthPercentage.toFixed(2));
        } catch (error) {
            console.error("Error fetching sales data:", error);
        }
    };

    // ✅ Fetch Repairs and Revenue Statistics
    const fetchStatistics = async () => {
        try {
            const repairsResponse = await getRepairs();
            const usersResponse = await getUsers();
            const invoicesResponse = await getInvoices();

            if (!repairsResponse || !usersResponse || !invoicesResponse.success) return;

            // ✅ Count Completed Repairs
            const completedRepairs = Array.isArray(repairsResponse)
                ? repairsResponse.filter((repair) => repair.payment_status === "Completed")
                : [];

            // ✅ Calculate Repair Revenue from Invoices
            const totalRepairRevenue = completedRepairs.reduce(
                (acc, repair) => acc + parseFloat(repair.total_amount),
                0
            );

            // ✅ Set updated stats
            setStats({
                totalSales: totalSales || 0,
                completedRepairs: completedRepairs.length || 0,
                totalRevenue: totalRepairRevenue || 0,
                totalUsers: Array.isArray(usersResponse) ? usersResponse.length : 0,
            });

            setRevenueGrowth((totalRepairRevenue / (totalSales || 1) * 100).toFixed(2)); // Prevent division by zero
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    return (
        <Section>
            <StatisticCard
                color="#EEF4FF"
                icon={<AiFillTag />}
                title="Total Guns in Stock"
                value={`${gunsInStock} Guns`}
            />
            <StatisticCard
                color="#FDF4F5"
                icon={<AiFillExperiment />}
                title="Total Sales"
                value={`RS. ${totalSales.toLocaleString()}`}
                // percentage={salesGrowth}
            />
            <StatisticCard
                color="#E4FFF4"
                icon={<AiFillDollarCircle />}
                title="Total Revenue from Repairs"
                value={`RS. ${stats.totalRevenue.toLocaleString()}`} // Displays repair revenue
                // percentage={revenueGrowth}
            />
        </Section>
    );
}

// ✅ Statistic Card Component
const StatisticCard = ({ color, icon, title, value, percentage }) => {
    return (
        <Card style={{ backgroundColor: color }}>
            <div className="design">
                <div className="logo">{icon}</div>
                <div className="content">
                    <h5>{value}</h5>
                </div>
            </div>
            <div className="total">
                <h6>{title}</h6>
                {percentage && (
                    <>
                        <span className={percentage >= 0 ? "t1" : "t2"}>
                            {percentage >= 0 ? "+" : ""}
                            {percentage}%
                        </span>
                        {percentage >= 0 ? (
                            <AiOutlineArrowUp className="svg1" />
                        ) : (
                            <AiOutlineArrowDown className="svg2" />
                        )}
                    </>
                )}
            </div>
        </Card>
    );
};

export default Statistic;

// ✅ Styled Components
const Section = styled.section`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    padding: 1rem;
    margin-top: 120px;
`;

const Card = styled.div`
    padding: 1rem;
    border-radius: 1rem;
    color: black;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: 0.3s ease-in-out;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

    &:hover {
        background-color: #d4e0ff;
        color: black;
    }

    .design {
        display: flex;
        align-items: center;
        gap: 1rem;

        .logo {
            background-color: white;
            border-radius: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 1rem;
            border: 1px solid #ddd;

            svg {
                font-size: 1.8rem;
            }
        }
    }

    .total {
        display: flex;
        align-items: center;
        gap: 0.2rem;
        margin-top: 20px;

        .svg1 { color: green; }
        .svg2 { color: red; }
        .t1 { color: green; }
        .t2 { color: red; }
        h6 { color: gray; }
    }
`;
